"use client";

import React, { useEffect, useState } from "react";
import { useTable, usePagination } from "react-table";
import { CSVLink } from "react-csv";
import DownloadIcon from "@mui/icons-material/Download";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { EmptyState, LoadingState } from "../UI/States";

const SubscribedUsers = () => {
  const [subscribedUsers, setSubscribedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchSubscribedUsers = async () => {
      try {
        const response = await fetch("/api/cloud/website/getSubscribedUsers");
        if (!response.ok) {
          throw new Error("Failed to fetch SubscribedUsers");
        }
        const data = await response.json();

        // Transform data structure
        const formattedData = data.map((user) => ({
          _id: user._id,
          entryDate: user.webleadEntry?.entryDate
            ? new Date(user.webleadEntry.entryDate).toLocaleDateString("en-US")
            : "N/A",
          customerName: user.webleadEntry?.customerName || "N/A",
          customerEmail: user.webleadEntry?.customerEmail || "N/A",
          customerPhone: user.webleadEntry?.customerPhone || "N/A",
        }));

        setSubscribedUsers(formattedData.reverse());
        setLoading(false);
      } catch (error) {
        console.error("Error fetching SubscribedUsers:", error);
        setErrorMessage("Unable to load SubscribedUsers. Please try again.");
        setLoading(false);
      }
    };

    fetchSubscribedUsers();
  }, []);

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: "Entry Date",
        accessor: "entryDate",
      },
      {
        Header: "Name",
        accessor: "customerName",
      },
      {
        Header: "Email",
        accessor: "customerEmail",
      },
      {
        Header: "Phone",
        accessor: "customerPhone",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: subscribedUsers,
      initialState: { pageIndex: 0, pageSize: 20 }, // Set default page size
    },
    usePagination
  );

  // CSV Headers
  const csvHeaders = [
    { label: "Entry Date", key: "entryDate" },
    { label: "Name", key: "customerName" },
    { label: "Email", key: "customerEmail" },
    { label: "Phone", key: "customerPhone" },
  ];

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold tabular-nums">
            {subscribedUsers.length} subscribed users
          </h2>
          <p className="app-muted text-sm">
            Export newsletter sign-ups or browse the list below.
          </p>
        </div>
        <CSVLink
          data={subscribedUsers}
          headers={csvHeaders}
          filename="subscribed_users.csv"
          className="app-btn app-btn-secondary self-start sm:self-auto"
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
          Export to CSV
        </CSVLink>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
        >
          {errorMessage}
        </div>
      )}

      <div className="flex w-full flex-col">
        {loading && <LoadingState label="Loading..." />}
        {!loading && subscribedUsers.length === 0 && (
          <div className="app-surface rounded-xl border">
            <EmptyState
              compact
              icon={MarkEmailReadOutlinedIcon}
              title="No subscribed users to display."
            />
          </div>
        )}
        {!loading && subscribedUsers.length > 0 && (
          <div className="app-surface overflow-hidden rounded-xl border">
            <div className="overflow-x-auto">
              <table {...getTableProps()} className="app-table">
                <thead>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th {...column.getHeaderProps()}>
                          {column.render("Header")}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            className="whitespace-nowrap"
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between gap-3 border-t app-divider px-4 py-3">
              <button
                type="button"
                onClick={previousPage}
                disabled={!canPreviousPage}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                Previous
              </button>
              <span className="app-muted text-center text-sm tabular-nums">
                Page <strong>{pageIndex + 1}</strong> of {pageOptions.length}
              </span>
              <button
                type="button"
                onClick={nextPage}
                disabled={!canNextPage}
                className="app-btn app-btn-secondary app-btn-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribedUsers;
